// src/components/common/ChatBubble.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, MessageCircle, Home, Building2, BedDouble, Users, PhoneCall, ChevronRight } from 'lucide-react';

/* ── Color tokens (matches Dewanta design system) ── */
const C = {
  amber:      '#d97706',
  amberLight: '#fef3c7',
  amberDark:  '#92400e',
  amberHover: '#b45309',
  white:      '#ffffff',
  gray50:     '#f9fafb',
  gray100:    '#f3f4f6',
  gray200:    '#e5e7eb',
  gray400:    '#9ca3af',
  gray500:    '#6b7280',
  gray600:    '#4b5563',
  gray700:    '#374151',
  gray900:    '#111827',
};

const menuItems = [
  {
    label: 'Manage my villa',
    sub: 'Villa management',
    icon: Building2,
    path: '/business',
    highlight: true,
  },
  {
    label: 'List my property',
    sub: 'Register your villa with us',
    icon: Home,
    path: '/contact',
  },
  {
    label: 'Book a villa',
    sub: 'Find your perfect stay',
    icon: BedDouble,
    path: '/villas',
  },
  {
    label: 'Explore our services',
    sub: 'What we offer',
    icon: Users,
    path: '/services',
  },
  {
    label: 'Get in touch',
    sub: 'Contact us directly',
    icon: PhoneCall,
    path: '/contact',
  },
];

const ChatBubble = () => {
  const [open, setOpen]         = useState(true);
  const [visible, setVisible]   = useState(false);
  const [hovBtn, setHovBtn]     = useState(false);
  const [hovItem, setHovItem]   = useState(null);
  const navigate                = useNavigate();

  /* pop-in delay on mount */
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const handleItem = (path) => {
    navigate(path);
    setOpen(false);
  };

  return (
    <>
      <style>{`
        @keyframes chatPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(217,119,6,0.55); }
          50%       { box-shadow: 0 0 0 14px rgba(217,119,6,0); }
        }
        @keyframes chatFadeIn {
          from { opacity: 0; transform: translateY(14px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)     scale(1);   }
        }
        @keyframes chatSlideIn {
          from { opacity: 0; transform: translateX(10px); }
          to   { opacity: 1; transform: translateX(0);    }
        }
        @keyframes dotBounce {
          0%, 80%, 100% { transform: translateY(0);     opacity: 1;   }
          40%           { transform: translateY(-5px);  opacity: 0.5; }
        }
        .chat-panel {
          animation: chatFadeIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both;
        }
        .chat-menu-item {
          animation: chatSlideIn 0.35s cubic-bezier(0.16,1,0.3,1) both;
        }
      `}</style>

      <div style={{
        position: 'fixed', bottom: 28, right: 28, zIndex: 9000,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 14,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.34,1.56,0.64,1)',
        fontFamily: "'DM Sans', sans-serif",
      }}>

        {/* ── Chat Panel ── */}
        {open && (
          <div className="chat-panel" style={{
            width: 310,
            backgroundColor: C.white,
            borderRadius: 22,
            boxShadow: '0 24px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.07)',
            overflow: 'hidden',
            border: `1px solid ${C.gray100}`,
          }}>

            {/* Header */}
            <div style={{
              background: `linear-gradient(135deg, ${C.gray900} 0%, #1f2937 100%)`,
              padding: '18px 20px',
              display: 'flex', alignItems: 'center', gap: 13,
              position: 'relative',
            }}>
              {/* Close */}
              <button
                onClick={() => setOpen(false)}
                style={{
                  position: 'absolute', top: 12, right: 12,
                  width: 28, height: 28, borderRadius: '50%', border: 'none',
                  backgroundColor: 'rgba(255,255,255,0.12)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: C.white, transition: 'background 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.22)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'}
              >
                <X size={13} />
              </button>

              {/* Avatar */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {/* <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${C.amber}, #f59e0b)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, boxShadow: '0 4px 12px rgba(217,119,6,0.45)',
                }}>
                  🧑‍💼
                </div> */}

                <img
                    src="/images/bubblechat.jpg"
                    alt="Dewa"
                    style={{
                        width: 48, height: 48, borderRadius: '50%',
                        objectFit: 'cover',
                        boxShadow: '0 4px 12px rgba(217,119,6,0.45)',
                    }}
                />
                {/* Online dot */}
                <div style={{
                  position: 'absolute', bottom: 1, right: 1,
                  width: 11, height: 11, borderRadius: '50%',
                  backgroundColor: '#22c55e',
                  border: `2px solid ${C.gray900}`,
                }} />
              </div>

              <div>
                <div style={{ color: C.white, fontWeight: 700, fontSize: 15, letterSpacing: '-0.01em' }}>
                  Dewa Juniarta
                </div>
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 1 }}>
                  Dewanta Villas Management
                </div>
              </div>
            </div>

            {/* Greeting bubble */}
            <div style={{ padding: '18px 18px 10px' }}>
              <div style={{
                backgroundColor: C.gray50,
                borderRadius: '4px 18px 18px 18px',
                padding: '13px 15px',
                fontSize: 13.5, color: C.gray700, lineHeight: 1.65,
                border: `1px solid ${C.gray100}`,
              }}>
                👋 Hi there! Welcome to <strong style={{ color: C.amberDark }}>Dewanta Villas Management</strong>.<br />
                How can Dewa help you today?
              </div>
              <div style={{ fontSize: 11, color: C.gray400, marginTop: 6, paddingLeft: 4 }}>
                Typically replies within minutes
              </div>
            </div>

            {/* Menu items */}
            <div style={{ padding: '6px 12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {menuItems.map((item, i) => {
                const Icon = item.icon;
                const isHov = hovItem === i;
                return (
                  <button
                    key={i}
                    className="chat-menu-item"
                    onClick={() => handleItem(item.path)}
                    onMouseEnter={() => setHovItem(i)}
                    onMouseLeave={() => setHovItem(null)}
                    style={{
                      animationDelay: `${i * 55}ms`,
                      display: 'flex', alignItems: 'center', gap: 11,
                      width: '100%', padding: '11px 14px',
                      borderRadius: 13, border: 'none', cursor: 'pointer',
                      textAlign: 'left', fontFamily: 'inherit',
                      backgroundColor: item.highlight
                        ? (isHov ? C.amberHover : C.amber)
                        : (isHov ? C.amberLight : C.gray50),
                      color: item.highlight ? C.white : (isHov ? C.amberDark : C.gray700),
                      boxShadow: item.highlight
                        ? (isHov ? '0 6px 18px rgba(217,119,6,0.45)' : '0 3px 10px rgba(217,119,6,0.28)')
                        : (isHov ? '0 2px 8px rgba(0,0,0,0.06)' : 'none'),
                      transform: isHov ? 'translateX(4px)' : 'translateX(0)',
                      transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
                      border: item.highlight ? 'none' : `1px solid ${isHov ? C.amberLight : C.gray200}`,
                    }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                      backgroundColor: item.highlight
                        ? 'rgba(255,255,255,0.2)'
                        : (isHov ? C.amber : C.amberLight),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'background 0.25s',
                    }}>
                      <Icon size={15} color={item.highlight ? C.white : (isHov ? C.white : C.amber)} strokeWidth={2} />
                    </div>

                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600,
                        color: item.highlight ? C.white : (isHov ? C.amberDark : C.gray800),
                      }}>
                        {item.label}
                      </div>
                      <div style={{
                        fontSize: 11,
                        color: item.highlight ? 'rgba(255,255,255,0.72)' : C.gray400,
                        marginTop: 1,
                      }}>
                        {item.sub}
                      </div>
                    </div>

                    <ChevronRight size={14}
                      color={item.highlight ? 'rgba(255,255,255,0.7)' : (isHov ? C.amber : C.gray400)}
                      style={{ transition: 'transform 0.2s', transform: isHov ? 'translateX(2px)' : 'none', flexShrink: 0 }}
                    />
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div style={{
              padding: '10px 18px 14px',
              borderTop: `1px solid ${C.gray100}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 5, height: 5, borderRadius: '50%', backgroundColor: C.amber,
                    animation: `dotBounce 1.4s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
              </div>
              <span style={{ fontSize: 11, color: C.gray400 }}>Powered by PT. Baswara Arta Santosa</span>
            </div>
          </div>
        )}

        {/* ── Toggle Button ── */}
        <button
          onClick={() => setOpen(v => !v)}
          onMouseEnter={() => setHovBtn(true)}
          onMouseLeave={() => setHovBtn(false)}
          style={{
            width: 58, height: 58, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: open
              ? `linear-gradient(135deg, ${C.gray700}, ${C.gray900})`
              : `linear-gradient(135deg, ${C.amber}, #f59e0b)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: open
              ? '0 8px 24px rgba(0,0,0,0.3)'
              : (hovBtn
                  ? '0 12px 30px rgba(217,119,6,0.55)'
                  : '0 6px 20px rgba(217,119,6,0.4)'),
            transform: hovBtn ? 'scale(1.1) translateY(-2px)' : 'scale(1)',
            animation: (!open && !hovBtn) ? 'chatPulse 2.8s ease-in-out infinite' : 'none',
            transition: 'all 0.35s cubic-bezier(0.34,1.56,0.64,1)',
          }}
          aria-label={open ? 'Close chat' : 'Open chat'}
        >
          <div style={{
            transform: open ? 'rotate(180deg) scale(1)' : 'rotate(0deg) scale(1)',
            transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {open
              ? <X size={22} color={C.white} />
              : <MessageCircle size={22} color={C.white} />
            }
          </div>
        </button>
      </div>
    </>
  );
};

export default ChatBubble;